// src/components/DashboardGroups/service.ts
import { apiService } from '@/services/api';
import { socketService } from '@/services/socketService';
import type { DashboardGroupData, DashboardGroupsRequestParams } from './types';
// DashboardGroupConversations import removed (unused)
import type { ConversationDetail } from '@/types/conversation';
import type { Dashboard, GetSendToElements } from '@/types/dashboard';

class DashboardGroupsService
{
  /**
   * Get initial dashboard group conversations
   * GET api/conversation/GetInitialDashboardGroupConversations
   *
   * Example params:
   * {
   *   "CustomFields": [],
   *   "PageNumber": 1,
   *   "PageSize": 130,
   *   "DashboardId": 1183,
   *   "Project": "35",
   *   "Filters": "[]",
   *   "SearchText": "",
   *   "LayersToHide": "",
   *   "DashboardViewTypeId": 1,
   *   "IsFilterPersonal": false
   * }
   */
  async getInitialDashboardGroupConversations(
    params: DashboardGroupsRequestParams,
  ): Promise<DashboardGroupData[]>
  {
    try
    {
      const response = await apiService.post<DashboardGroupData[]>(
        '/conversation/GetInitialDashboardGroupConversations',
        params,
        { timeout: 120000 },
      );
      return response;
    }
    catch (error)
    {
      console.error('Error getting initial dashboard group conversations:', error);
      return [];
    }
  }

  /**
   * Move conversations between dashboard groups via POST.
   * ConversationIds must contain ALL conversation IDs in the destination group
   * in their final display order.
   *
   * POST api/conversation/InsertDashboardGroupMultipleConversations
   */
  async moveConversationsToGroup(params: {
    dashboardId: number;
    destinationGroupId: number;
    originGroupId: number;
    /** The conversation ID being moved */
    movedConversationId: number;
    /** Parent conversation ID for hierarchy (0 = root level) */
    parentConversationId?: number;
  }): Promise<any[]>
  {
    try
    {
      const parentId = params.parentConversationId ?? 0;
      const qs = new URLSearchParams();
      qs.set('DashboardId', String(params.dashboardId));
      qs.set('DashboardGroupId', String(params.destinationGroupId));
      qs.set('ConversationIds', String(params.movedConversationId));
      qs.set('ParentConversationId', String(parentId));
      qs.set('DashboardGroupIdOrigin', String(params.originGroupId));
      qs.set('RemovedFromParent', 'true');
      qs.set('UpdateParent', 'false');
      qs.set('PreviousParentId', '0');
      qs.set('pParentId', '0');
      qs.set('pDashboardId', String(params.dashboardId));
      qs.set('pDashboardGroupId', String(params.originGroupId));
      qs.set('iConversationId', String(params.movedConversationId));
      qs.set('nDashboardId', String(params.dashboardId));
      qs.set('nDashboardGroupId', String(params.destinationGroupId));
      qs.set('nParentId', parentId ? String(parentId) : 'null');
      qs.set('socketConnectionId', socketService.connectionId);

      const response = await apiService.post<any[]>(
        `/conversation/InsertDashboardGroupMultipleConversations?${qs.toString()}`,
        null,
      );
      return response || [];
    }
    catch (error)
    {
      console.error('Error moving conversations to group:', error);
      return [];
    }
  }

  /**
   * Update dashboard group name
   */
  /**
   * Update dashboard group (name, detailed view, etc)
   * PUT api/dashboardgroup/updatedashboardgroup
   */
  async updateDashboardGroup(params: {
    DashboardGroupId: number;
    DashboardId: number;
    Name: string;
    IsDefault: boolean;
    SizeX: number;
    SizeY: number;
    Row: number;
    Col: number;
    showAllDetails: boolean;
    Conversations: any[];
    positionX: number;
    TotalItems: number;
    col: number;
    minSizeX: number;
    showProjectName: boolean;
    isLoading: boolean;
    minSizeY: number;
    row: number;
    sizeX: number;
    sizeY: number;
    draftName: string;
    editGroupName: boolean;
    SocketConnectionId: string;
  }): Promise<boolean>
  {
    try
    {
      await apiService.put('/dashboardgroup/updatedashboardgroup', params);
      return true;
    }
    catch (error)
    {
      console.error('Error updating dashboard group:', error);
      return false;
    }
  }

  /**
   * Update dashboard group positions after reorder
   */
  async updateDashboardGroupPosition(dashboardGroups: DashboardGroupData[]): Promise<void>
  {
    try
    {
      await apiService.put<void>('/dashboardgroup/updatedashboardgroupposition', dashboardGroups);
    }
    catch (error)
    {
      console.error('Error set new order of groups:', error);
      throw error;
    }
  }

  /**
   * Create a new dashboard group (column)
   * POST api/dashboardgroup/
   */
  async createDashboardGroup(params: {
    DashboardId: number;
    Conversations: any[];
    Name: string;
    positionX: number;
    col: number;
    row: number;
    sizeX: number;
    sizeY: number;
    isNew: boolean;
    TotalItems: number;
    SocketConnectionId: string;
  }): Promise<DashboardGroupData | null>
  {
    try
    {
      const response = await apiService.post<DashboardGroupData>('/dashboardgroup/', params);
      return response;
    }
    catch (error)
    {
      console.error('Error creating dashboard group:', error);
      return null;
    }
  }

  /**
   * Get more conversations for a specific group (pagination)
   */
  async getGroupConversations(
    dashboardGroupId: number,
    pageNumber: number,
    pageSize: number = 30,
  ): Promise<DashboardGroupData | null>
  {
    try
    {
      const response = await apiService.get<DashboardGroupData>(
        `/dashboardgroup/GetConversations?dashboardGroupId=${dashboardGroupId}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error getting group conversations:', error);
      return null;
    }
  }

  /**
   * Get more conversations for a specific dashboard group column (infinite scroll)
   * POST api/conversation/getconversationsbydashboardgroup
   */
  async getMoreGroupConversations(params: {
    CustomFields: any[];
    PageNumber: number;
    PageSize: number;
    DashboardGroupId: number;
    SearchText: string;
    LayersToHide: string;
    ConfigValueId?: number;
    /** Project id: required on the Projects view so the dynamic column keeps filtering by project */
    Project?: string;
    /** Flattened standard filters (e.g. Item Type), same as the initial dashboard load */
    [key: string]: any;
  }): Promise<ConversationDetail[]>
  {
    try
    {
      const response = await apiService.post<ConversationDetail[]>(
        '/conversation/getconversationsbydashboardgroup',
        params,
      );
      return response || [];
    }
    catch (error)
    {
      console.error('Error getting more group conversations:', error);
      return [];
    }
  }

  /**
   * Get hierarchy of conversations for a given parent card
   * POST api/conversation/GetConversationHierarchy
   */
  async getConversationHierarchy(params: {
    ConversationId: number;
    DashboardGroupId: number;
    DashboardId: number;
    CustomFields?: any[];
    Filters?: string;
    LayersToHide?: string;
    PageNumber?: number;
    PageSize?: number;
    SearchText?: string;
  }): Promise<ConversationDetail[]>
  {
    try
    {
      const defaultParams = {
        CustomFields: [],
        Filters: '[]',
        LayersToHide: '0',
        PageNumber: 0,
        PageSize: 1000,
        SearchText: '',
      };

      const response = await apiService.post<ConversationDetail[]>(
        '/conversation/GetConversationHierarchy',
        { ...defaultParams, ...params },
      );
      return response || [];
    }
    catch (error)
    {
      console.error('Error getting conversation hierarchy:', error);
      return [];
    }
  }
  /**
   * Get menu items by config value (for IconPicker)
   * GET api/menu/getByConfigValuePerson
   */
  async getMenuByConfigValue(projectId?: number | null): Promise<any[]>
  {
    try
    {
      const url = projectId
        ? `/menu/getByConfigValuePerson?projectId=${projectId}`
        : '/menu/getByConfigValuePerson?projectId=null';
      const response = await apiService.get<any[]>(url);
      return response;
    }
    catch (error)
    {
      console.error('Error getting menu by config value:', error);
      return [];
    }
  }

  /**
   * Get Elements to send
   */
  async GetSendToElements(DashboardGroupId: number): Promise<GetSendToElements[]>
  {
    try
    {
      const response = await apiService.get<GetSendToElements[]>(
        `/DashboardGroup/GetSendToElements?dashboardGroupId=${DashboardGroupId}`,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error delete dashboard group:', error);
      throw error;
    }
  }

  /**
   * Validate if exist the cards in the new dashboard
   */
  async AddDashboardGroupByParametersValidateCompatibleItems(
    dashboardId: number,
    dashboardGroupId: number,
    conversationId?: string,
    dashboardGroupIdSend?: number,
    includeAllConversations?: boolean,
    configValueId?: number,
    projectId?: number,
    ParentConversationId?: number,
    dashboardGroupOrigin?: number,
  ): Promise<Dashboard>
  {
    try
    {
      const response = await apiService.get<Dashboard>(
        `dashboard/AddDashboardGroupByParametersValidateCompatibleItems?dashboardId=${dashboardId}&dashboardGroupId=${dashboardGroupId}&conversationId=${conversationId || 0}&dashboardGroupIdSend=${dashboardGroupIdSend || 0}&includeAllConversations=${includeAllConversations || false}&configValueId=${configValueId || 0}&projectId=${projectId || 0}&ParentConversationId=${ParentConversationId || 0}&dashboardGroupOrigin=${dashboardGroupOrigin || 0}`,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error GetConversationsDuplicated:', error);
      throw error;
    }
  }

  /**
   * Validate if exist the cards in the new dashboard
   */
  async GetConversationsDuplicated(
    dashboardId: number,
    dashboardGroupId: number | null,
    conversationIds?: string,
  ): Promise<any>
  {
    try
    {
      const groupParam = dashboardGroupId !== null ? dashboardGroupId : 'null';
      const response = await apiService.get<any>(
        `/dashboard/GetConversationsDuplicated?dashboardId=${dashboardId}&dashboardGroupId=${groupParam}&conversationIds=${conversationIds ?? ''}`,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error GetConversationsDuplicated:', error);
      throw error;
    }
  }

  async addDashboardGroupByParametersMultiple(params: {
    DashboardId: number;
    DashboardGroupIdSend: number;
    IncludeAllConversations: boolean;
    Conversations: Array<{
      DashboardGroupId: number;
      ConversationId: number;
      ParentConversationId: number;
    }>;
  }): Promise<any>
  {
    try
    {
      const response = await apiService.post<any>(
        '/dashboard/AddDashboardGroupByParametersMultiple',
        params,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error AddDashboardGroupByParametersMultiple:', error);
      throw error;
    }
  }

  async setConversationsAsViewed(conversationIdsString: string): Promise<void>
  {
    try
    {
      await apiService.put(
        `alert/SetConversationsAsViewed?conversationIdsString=${conversationIdsString}`,
        {},
      );
    }
    catch (error)
    {
      console.error('Error SetConversationsAsViewed:', error);
    }
  }

  /**
   * Get layers by config value
   * GET api/layer/GetLayersByConfigValueId
   */
  async getLayersByConfigValue(configValueId: number, projectId?: number | null): Promise<any[]>
  {
    try
    {
      let url = `/layer/GetLayersByConfigValueId?configValueId=${configValueId}`;
      if (projectId) url += `&projectId=${projectId}`;
      const response = await apiService.get<any[]>(url);
      return response;
    }
    catch (error)
    {
      console.error('Error getting layers by config value:', error);
      return [];
    }
  }

  /**
   * Get additional layers available for a card type (mirrors CardConversationLayers second call)
   * GET api/layer/GetLayersForCardBy
   */
  async getLayersForCardBy(configValueId: number, projectId?: number | null): Promise<any[]>
  {
    try
    {
      let url = `/layer/GetLayersForCardBy?configValueId=${configValueId}`;
      if (projectId) url += `&projectId=${projectId}`;
      const response = await apiService.get<any[]>(url);
      return response;
    }
    catch (error)
    {
      console.error('Error getting layers for card by:', error);
      return [];
    }
  }

  /**
   * Get empty custom fields for new task
   * GET api/configvalue/GetEmptyCustomFieldsByConfigValueId
   */
  async getEmptyCustomFields(configValueId: number, projectId?: number): Promise<any[]>
  {
    try
    {
      const params = new URLSearchParams();
      params.append('configValueId', String(configValueId));
      if (projectId)
      {
        params.append('ProjectId', String(projectId));
      }
      const response = await apiService.get<any[]>(
        `/configvalue/GetEmptyCustomFieldsByConfigValueId?${params.toString()}`,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error getting empty custom fields:', error);
      return [];
    }
  }

  /**
   * Create a new task (inline)
   * POST api/conversation/CreateTask
   */
  async createTask(params: {
    dashboardId: number;
    dashboardGroupId: number;
    title: string;
    configValueId: number;
    layerId: number;
    customFields?: any[];
    socketConnectionId?: string;
  }): Promise<any>
  {
    try
    {
      const response = await apiService.post<any>('/conversation/CreateTask', params);
      return response;
    }
    catch (error)
    {
      console.error('Error creating task:', error);
      return null;
    }
  }
  /**
   * Create a new conversation
   * POST api/conversation/
   */
  async createConversation(params: any): Promise<any>
  {
    try
    {
      const response = await apiService.post<any>('/conversation/', params);
      return response;
    }
    catch (error)
    {
      console.error('Error creating conversation:', error);
      return null;
    }
  }

  /**
   * Insert conversation into dashboard group
   * POST api/conversation/InsertDashboardGroupConversation
   */
  async insertDashboardGroupConversation(params: {
    DashboardId: number;
    DashboardGroupId: number;
    ConversationId: number;
    ParentConversationId: number;
    PreviousParentId: number | null;
    socketConnectionId: string;
  }): Promise<boolean>
  {
    try
    {
      const qs = new URLSearchParams();
      qs.set('DashboardId', String(params.DashboardId));
      qs.set('DashboardGroupId', String(params.DashboardGroupId));
      qs.set('ConversationId', String(params.ConversationId));
      qs.set('ParentConversationId', String(params.ParentConversationId));
      qs.set(
        'PreviousParentId',
        params.PreviousParentId !== null ? `{${params.PreviousParentId}}` : '{}',
      );
      qs.set('socketConnectionId', params.socketConnectionId);

      await apiService.post(
        `/conversation/InsertDashboardGroupConversation?${qs.toString()}`,
        null,
      );
      return true;
    }
    catch (error)
    {
      console.error('Error inserting dashboard group conversation:', error);
      return false;
    }
  }

  /**
   * Update percent dashboard group conversations
   * POST api/conversation/UpdatePercentDashboardGroupConversations
   */
  async updatePercentDashboardGroupConversations(params: {
    dashboardGroupConvs: { DashboardGroupId: number }[];
    DashboardGroupIdDefault?: number;
  }): Promise<boolean>
  {
    try
    {
      await apiService.post('/conversation/UpdatePercentDashboardGroupConversations', params);
      return true;
    }
    catch (error)
    {
      console.error('Error updating percent dashboard group conversations:', error);
      return false;
    }
  }
}

export const dashboardGroupsService = new DashboardGroupsService();
